import { useState } from 'react';
import { Phone, cx } from '../../ui';
import type { UnitMock } from './data';

// A fictional brand's unit page, as the owner/dealer/engineer sees it after one scan.
// White-labelled: the brand leads, "Powered by BrandedMark" sits quietly at the foot.
export function UnitPhone({ unit, className }: { unit: UnitMock; className?: string }) {
  const light = unit.brandInk !== '#fff' && unit.brandInk !== '#F7F5F0';
  const [photo, setPhoto] = useState(unit.photo);
  return (
    <Phone className={className}>
      <div className="flex h-full flex-col text-[11.5px] leading-snug text-[#2D2D2D]" style={{ fontFamily: 'Poppins, Arial, sans-serif' }}>
        {/* brand header */}
        <div className="px-4 pb-4 pt-10" style={{ background: unit.brandColor, color: unit.brandInk }}>
          <div className="flex items-center justify-between">
            <span className="text-[12.5px] font-extrabold tracking-[-0.01em]">{unit.brand}</span>
            <span className="rounded-full px-2 py-[3px] text-[9.5px] font-semibold" style={{ background: light ? 'rgba(45,45,45,.08)' : 'rgba(255,255,255,.14)' }}>
              EN
            </span>
          </div>
          <div className="mt-4 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.12em] opacity-70">{unit.kind}</div>
              <div className="mt-0.5 text-[17px] font-bold leading-tight tracking-[-0.02em]">{unit.product}</div>
              <div className="mt-1.5 font-mono text-[10px] opacity-80">{unit.serial}</div>
            </div>
            {photo && (
              <img src={photo} alt="" width={120} height={120} className="h-[60px] w-[60px] shrink-0 rounded-xl bg-white object-cover" onError={() => setPhoto(undefined)} />
            )}
          </div>
          <div
            className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold"
            style={{ background: unit.status.tone === 'ok' ? '#E3F4E9' : '#FBF0D9', color: unit.status.tone === 'ok' ? '#1F6B3D' : '#7A5A12' }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: unit.status.tone === 'ok' ? '#2F9E58' : unit.accent }} />
            {unit.status.label}
          </div>
        </div>
        {/* owner / channel */}
        <div className="grid grid-cols-2 border-b border-[#ECEAE4] bg-[#F7F5F0]">
          {unit.owner.map((o, i) => (
            <div key={o.k} className={cx('px-4 py-2.5', i === 0 && 'border-r border-[#ECEAE4]')}>
              <div className="text-[9.5px] uppercase tracking-[0.08em] text-[#8A877E]">{o.k}</div>
              <div className="mt-0.5 truncate text-[11px] font-semibold">{o.v}</div>
            </div>
          ))}
        </div>
        {/* records */}
        <ul className="flex-1 divide-y divide-[#F0EEE8] px-4">
          {unit.records.map((r) => (
            <li key={r.k} className="flex items-center gap-3 py-2.5">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg" style={{ background: '#F2F0EA' }}>
                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: unit.accent }} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[11.5px] font-semibold">{r.k}</span>
                <span className="block truncate text-[10px] text-[#77746B]">{r.v}</span>
              </span>
              {r.tag && <span className="rounded-md border border-[#E4E1D8] px-1.5 py-[2px] text-[9px] font-semibold text-[#77746B]">{r.tag}</span>}
            </li>
          ))}
        </ul>
        {/* ask */}
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-full border border-[#E4E1D8] bg-[#FBFAF7] px-3 py-2 text-[10.5px] text-[#8A877E]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: unit.accent }} />
          Ask about this {unit.kind.split(',')[0].toLowerCase()}
        </div>
        {/* actions */}
        <div className="space-y-1.5 px-4 pb-3 pt-1">
          <div className="rounded-xl py-2.5 text-center text-[11.5px] font-semibold" style={{ background: light ? unit.accent : unit.brandColor, color: '#fff' }}>
            {unit.actions[0]}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {unit.actions.slice(1).map((a) => (
              <div key={a} className="rounded-xl border border-[#E4E1D8] py-2 text-center text-[10.5px] font-semibold">
                {a}
              </div>
            ))}
          </div>
          <div className="pt-1.5 text-center text-[8.5px] text-[#A3A096]">Powered by BrandedMark</div>
        </div>
      </div>
    </Phone>
  );
}
